import { useContext, useReducer, useState } from 'react'

import {
  faCheck,
  faClockRotateLeft,
  faClose,
  faCopy,
  faEllipsisV,
  faFloppyDisk,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Radio,
  TextField,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Alert } from '@material-ui/lab'
import { NonDismissibleDialog } from '@scoir/modals'
import { Tooltip } from '@scoir/tooltips'

import { HSExploreView } from './types'
import { ViewsContext } from './useViews'

type HSExploreViewMenuProps = {
  defaultViewId?: string
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  hasUnsavedChanges?: boolean
}
const { Body, Footer } = NonDismissibleDialog
const useStyles = makeStyles((theme) => ({
  body: {
    width: 420,
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down('sm')]: {
      maxWidth: 380,
    },
  },
  unsaved: {
    fontSize: theme.typography.pxToRem(13),
  },
  iconBtn: {
    padding: theme.spacing(1.5),
    color: 'rgba(0, 0, 0, 0.54)',
    overflow: 'hidden',
    borderRadius: `calc(50% + ${theme.spacing(1.5)}px)`,
  },
  footerContainer: {
    width: '100%',
    position: 'sticky',
    bottom: 0,
    zIndex: 1,
    backgroundColor: theme.palette.common.white,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  renameBtn: {
    margin: `0px ${theme.spacing(1)}px`,
  },
  headerContainer: {
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}))

type ViewState = {
  clickedView: HSExploreView | undefined
  openSaveModal: boolean
  openDeleteModal: boolean
  menuOpen: boolean
  renameViewOpenID?: string | null
  renameViewValue: string
}

type ViewAction =
  | { type: 'open-save-modal'; view: HSExploreView }
  | { type: 'open-delete-modal'; view: HSExploreView }
  | { type: 'open-menu'; view: HSExploreView }
  | { type: 'close-modals' }
  | { type: 'close-menu' }
  | { type: 'set-rename-view-open-id'; value: string | null }
  | { type: 'update-rename-view-value'; value: string }

const initialState: ViewState = {
  clickedView: undefined,
  openSaveModal: false,
  openDeleteModal: false,
  menuOpen: false,
  renameViewValue: '',
}

function viewReducer(state: ViewState, action: ViewAction): ViewState {
  switch (action.type) {
    case 'open-save-modal':
      return {
        ...state,
        clickedView: action.view,
        openSaveModal: true,
      }
    case 'open-delete-modal':
      return {
        ...state,
        clickedView: action.view,
        openDeleteModal: true,
      }
    case 'open-menu':
      return {
        ...state,
        clickedView: action.view,
        menuOpen: true,
      }
    case 'close-modals':
      return {
        ...state,
        openSaveModal: false,
        openDeleteModal: false,
      }
    case 'close-menu':
      return {
        ...state,
        menuOpen: false,
      }
    case 'update-rename-view-value':
      return {
        ...state,
        renameViewValue: action.value,
      }
    case 'set-rename-view-open-id':
      return {
        ...state,
        renameViewOpenID: action.value,
        renameViewValue: state.clickedView?.name || '',
      }
    default:
      return state
  }
}

const HSExploreViewMenu = ({ anchorEl, open, onClose, hasUnsavedChanges }: HSExploreViewMenuProps) => {
  const classes = useStyles()
  const { activeView, views, changeActiveView, updateView, createView, deleteView, updateDefaultView } =
    useContext(ViewsContext)
  const [state, dispatch] = useReducer(viewReducer, initialState)
  const { clickedView, renameViewOpenID, renameViewValue, openSaveModal } = state
  const isDefaultView = (view: HSExploreView) => view.id === views.defaultViewId
  const isActiveView = (view: HSExploreView) => view.id === activeView?.id
  const isStandardView = (view: HSExploreView) => view?.id?.startsWith('_default')
  const [anchor, setAnchor] = useState<null | HTMLButtonElement>(null)
  const openModal = (view: HSExploreView) => {
    dispatch({ type: 'open-save-modal', view })
  }

  return (
    <>
      <Popover
        disableScrollLock
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={() => {
          dispatch({ type: 'set-rename-view-open-id', value: null })
          onClose()
        }}
        role="dialog"
      >
        <div className={classes.body}>
          <div className={classes.headerContainer}>
            <Typography variant="subtitle2">Views</Typography>
            <IconButton
              title="Close"
              aria-label="Close"
              size="small"
              onClick={() => {
                dispatch({ type: 'set-rename-view-open-id', value: null })
                onClose()
              }}
            >
              <FontAwesomeIcon icon={faClose} />
            </IconButton>
          </div>
          <div className={classes.body}>
            <List
              dense
              disablePadding
            >
              {views?.views?.map((view: HSExploreView) => {
                const ariaLabelId = `table-view-item-${view.id}`
                return (
                  <ListItem
                    key={view.id}
                    dense
                    button
                    onClick={() => {
                      if (view.id && view.id !== renameViewOpenID) {
                        changeActiveView(view)
                      }
                    }}
                  >
                    {renameViewOpenID === view.id && clickedView ? (
                      <>
                        <TextField
                          title={`editing-${clickedView.name}`}
                          id="Rename View"
                          label="Rename"
                          aria-label={`editing-${clickedView.name}`}
                          value={renameViewValue}
                          onChange={(e) => {
                            e.stopPropagation()
                            const value = e.target.value
                            dispatch({ type: 'update-rename-view-value', value })
                          }}
                          variant="outlined"
                          size="small"
                          autoFocus
                          fullWidth
                          inputProps={{ maxLength: 40 }}
                        />
                        <IconButton
                          className={classes.renameBtn}
                          edge="end"
                          title="Confirm Change"
                          aria-label="Confirm Change"
                          size="small"
                          onClick={() => {
                            updateView({ ...clickedView, name: renameViewValue })
                            dispatch({ type: 'set-rename-view-open-id', value: null })
                          }}
                        >
                          <FontAwesomeIcon icon={faCheck} />
                        </IconButton>
                        <IconButton
                          className={classes.renameBtn}
                          edge="end"
                          title="Cancel Change"
                          aria-label="Cancel Change"
                          size="small"
                          onClick={() => dispatch({ type: 'set-rename-view-open-id', value: null })}
                        >
                          <FontAwesomeIcon icon={faXmark} />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <ListItemIcon>
                          <Radio
                            checked={activeView && isActiveView(view)}
                            color="primary"
                            disableRipple={false}
                            inputProps={{
                              'aria-labelledby': ariaLabelId,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          id={ariaLabelId}
                          primaryTypographyProps={{
                            style: {
                              maxWidth: isActiveView(view) ? '50%' : '90%',
                              overflow: isActiveView(view) ? 'hidden' : 'auto',
                              overflowWrap: isActiveView(view) ? 'break-word' : 'normal',
                            },
                          }}
                          primary={view.name}
                          secondary={isDefaultView(view) ? 'Default view' : undefined}
                        />
                        <ListItemSecondaryAction>
                          <Box display="flex">
                            {view.id === activeView?.id && (
                              <>
                                <Tooltip title={hasUnsavedChanges ? 'Revert to Last Save' : 'No Changes to Revert'}>
                                  <span>
                                    <IconButton
                                      className={classes.iconBtn}
                                      aria-label="Revert To Last Save"
                                      size="small"
                                      disabled={!hasUnsavedChanges}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        changeActiveView(view)
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faClockRotateLeft} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Save as New View">
                                  <IconButton
                                    className={classes.iconBtn}
                                    aria-label="Save as New View"
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      openModal(view)
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faCopy} />
                                  </IconButton>
                                </Tooltip>
                                {!view.id?.startsWith('_default') && (
                                  <Tooltip title={hasUnsavedChanges ? 'Save Changes' : 'No Unsaved Changes'}>
                                    <span>
                                      <IconButton
                                        className={classes.iconBtn}
                                        aria-label="Save Changes"
                                        size="small"
                                        disabled={!hasUnsavedChanges}
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          updateView(activeView)

                                          onClose()
                                        }}
                                      >
                                        <FontAwesomeIcon icon={faFloppyDisk} />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                              </>
                            )}

                            <Tooltip title="More Actions">
                              <IconButton
                                className={classes.iconBtn}
                                edge="end"
                                aria-label="Context Menu"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setAnchor(e.currentTarget)
                                  dispatch({ type: 'open-menu', view })
                                }}
                              >
                                <FontAwesomeIcon icon={faEllipsisV} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </ListItemSecondaryAction>
                      </>
                    )}
                  </ListItem>
                )
              })}
            </List>
            <div className={classes.footerContainer}>
              {hasUnsavedChanges && (
                <Alert
                  variant="standard"
                  severity="info"
                >
                  <Typography
                    variant="body2"
                    className={classes.unsaved}
                  >
                    Your current view has unsaved changes.
                  </Typography>
                </Alert>
              )}
            </div>
          </div>
        </div>
      </Popover>
      <Menu
        open={state.menuOpen}
        onClose={() => {
          dispatch({ type: 'close-menu' })
        }}
        onClick={() => {
          dispatch({ type: 'close-menu' })
        }}
        anchorEl={anchor}
      >
        {clickedView && (
          <Box key={`view-menu-${clickedView?.id}`}>
            <MenuItem
              onClick={() => {
                updateDefaultView(clickedView?.id)
              }}
            >
              Set as Default
            </MenuItem>
            {!isStandardView(clickedView) && (
              <>
                <MenuItem
                  onClick={() => {
                    dispatch({ type: 'set-rename-view-open-id', value: clickedView.id })
                  }}
                >
                  Rename
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    dispatch({ type: 'open-delete-modal', view: clickedView })
                  }}
                >
                  Delete
                </MenuItem>
              </>
            )}
          </Box>
        )}
      </Menu>

      {clickedView && (
        <>
          <SaveViewModal
            isOpen={openSaveModal}
            view={activeView}
            onSubmit={(view: HSExploreView): void => {
              dispatch({ type: 'close-modals' })
              const { id, ...newView } = view
              createView(newView)
            }}
            onDelete={(viewId: string) => {
              deleteView(viewId)
            }}
            onUpdate={(view: HSExploreView) => {
              updateView(view)
            }}
            onClose={() => dispatch({ type: 'close-modals' })}
          />
          <DeleteViewModal
            isOpen={state.openDeleteModal}
            onClose={() => dispatch({ type: 'close-modals' })}
            onConfirm={(viewId: string) => {
              deleteView(viewId)
              dispatch({ type: 'close-modals' })
            }}
            label={clickedView.name}
            viewId={clickedView.id}
          />
        </>
      )}
    </>
  )
}
type DeleteViewModalProps = {
  isOpen: boolean
  label: string
  onClose: () => void
  onConfirm: (viewId: string) => void
  viewId?: string
}

const DeleteViewModal = ({ isOpen, onClose, onConfirm, label, viewId }: DeleteViewModalProps) => (
  <NonDismissibleDialog
    open={isOpen}
    onClose={onClose}
    scroll="paper"
    headerText="Confirm Delete"
  >
    <Body>
      <Typography>
        Are you sure you want to delete <strong>{label}</strong> view?
      </Typography>
    </Body>
    <Footer>
      <Footer.CloseButton>No</Footer.CloseButton>
      <Footer.PrimaryActionButton onClick={() => viewId && onConfirm(viewId)}>Yes</Footer.PrimaryActionButton>
    </Footer>
  </NonDismissibleDialog>
)

type SaveViewModalContentProps = {
  view: HSExploreView
  onClose: () => void
  onSubmit: (view: HSExploreView) => void
}

const SaveViewModalContent = ({ view, onClose, onSubmit }: SaveViewModalContentProps) => {
  const [viewLabel, setViewLabel] = useState('')
  const [makeDefault, setMakeDefault] = useState(false)
  return (
    <>
      <Body>
        <Box pb={1}>
          <Typography variant="body1">
            Create a new saved view based on your current filters and column visibility.
          </Typography>
        </Box>
        <TextField
          autoFocus
          margin="dense"
          label="Name"
          id="save-view-modal-view-label"
          aria-label="Name"
          type="text"
          fullWidth
          variant="outlined"
          value={viewLabel}
          inputProps={{ maxLength: 40 }}
          onChange={(e) => {
            const value = e.target.value
            setViewLabel(value)
          }}
        />
        <FormControlLabel
          id="make-default"
          control={
            <Checkbox
              checked={makeDefault}
              onChange={(e) => setMakeDefault(e.target.checked)}
              color="primary"
            />
          }
          label="Make this my default view"
        />
      </Body>
      <Footer>
        <Box
          display="flex"
          justifyContent="space-between"
          width="100%"
        >
          <Box>
            <Footer.CloseButton>Cancel</Footer.CloseButton>
            &nbsp;
            <Footer.PrimaryActionButton
              onClick={() => {
                const newView = {
                  ...view,
                  name: viewLabel,
                  isDefault: makeDefault,
                }
                onSubmit(newView)
                onClose()
              }}
              disabled={!viewLabel}
            >
              Add
            </Footer.PrimaryActionButton>
          </Box>
        </Box>
      </Footer>
    </>
  )
}

export const SaveViewModal = ({ isOpen, onClose, ...rest }: SaveViewModalProps) => (
  <NonDismissibleDialog
    open={isOpen}
    onClose={onClose}
    scroll="paper"
    headerText={'Save View'}
  >
    <SaveViewModalContent
      onClose={onClose}
      {...rest}
    />
  </NonDismissibleDialog>
)
type SaveViewModalProps = {
  isOpen: boolean
  view: HSExploreView
  onClose: () => void
  onDelete: (id: string) => void
  onUpdate: (view: HSExploreView) => void
  onSubmit: (view: HSExploreView) => void
}
export default HSExploreViewMenu
